import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SubscribeSection from "@/pages/home/components/SubscribeSection";
import usePageMeta from "@/hooks/usePageMeta";
import { siteStats, testimonials } from "@/mocks/news";

const PRINCIPLES = [
  {
    icon: "ri-file-shield-2-line",
    title: "先事实，后背景",
    desc: "每篇内容先交代可核对的事实与官方表述，再补充背景信息，不让观点跑在事实前面。",
  },
  {
    icon: "ri-scales-3-line",
    title: "多方说法并列",
    desc: "当事各方的表述会同时呈现，不替任何一方下结论，也不为了流量放大单方声音。",
  },
  {
    icon: "ri-history-line",
    title: "时间线优先",
    desc: "突发类内容一律附时间线，谁先谁后一目了然，避免被掐头去尾的截图带偏。",
  },
  {
    icon: "ri-error-warning-line",
    title: "不确定就标注",
    desc: "信息尚待核实时会明确标注，宁可慢一步，也不把猜测写成结论。",
  },
];

const MILESTONES = [
  { date: "2024 年春", title: "瓜田速递上线", desc: "以突发专报为核心，只做一件事：把正在发生的事第一时间说清楚。" },
  { date: "2025 年", title: "增设深度瓜情", desc: "在速度之外补上脉络，开始提供时间线与多方说法的完整梳理。" },
  { date: "2026 年", title: "简报订阅上线", desc: "把当天最值得知道的进展整合成一封邮件，让重要瓜情不再被错过。" },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 品牌定位与内容原则",
    description:
      "了解瓜田速递的品牌定位、发展理念与内容原则。我们专注突发专报与紧急插播，坚持先事实后背景、多方说法并列，把重大瓜情的完整脉络交给你。",
    keywords: "瓜田速递,关于我们,内容原则,突发专报,吃瓜前线",
    path: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于瓜田速递",
        url: "https://cllcr.cn/about",
        inLanguage: "zh-CN",
        mainEntity: {
          "@type": "Organization",
          name: "瓜田速递",
          url: "https://cllcr.cn/",
          email: "tips@guatian-express.com",
          description: "面向泛娱乐人群的资讯速递站，提供突发专报、紧急插播、头条专递与深度瓜情。",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://cllcr.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://cllcr.cn/about" },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-foreground-950">
          <div className="w-full px-4 md:px-8 py-12 md:py-20">
            <div className="[&_a]:text-background-200/70 [&_span]:text-background-100">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            </div>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
              <div className="lg:col-span-8">
                <p className="text-xs font-label text-accent-400 tracking-widest mb-4">ABOUT US</p>
                <h1 className="font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight text-balance">
                  我们只做一件事
                  <span className="block mt-2 text-accent-400">把瓜讲清楚</span>
                </h1>
                <p className="mt-6 text-base text-background-200/85 leading-relaxed max-w-3xl">
                  瓜田速递是一个围绕文娱动态展开的资讯速递站。我们不生产情绪，也不制造对立，
                  而是把散落在各处的信息收拢起来，按时间排好，标明来源与不确定之处，
                  让你用自己的判断去吃每一个瓜。
                </p>
              </div>
              <div className="lg:col-span-4">
                <dl className="grid grid-cols-2 gap-x-6 gap-y-6">
                  {siteStats.map((stat) => (
                    <div key={stat.label}>
                      <dt className="text-xs font-label text-background-200/60">{stat.label}</dt>
                      <dd className="mt-1 font-heading text-2xl font-black text-background-50">
                        {stat.value}
                        <span className="ml-0.5 text-sm font-body font-normal text-background-200/60">
                          {stat.unit}
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>

        <section aria-label="品牌定位" className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 py-12 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-5">
                <p className="text-xs font-label text-primary-600 tracking-widest mb-3">POSITIONING</p>
                <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 leading-snug">
                  为谁而做，做给谁看
                </h2>
              </div>
              <div className="lg:col-span-7 space-y-5">
                <p className="text-[15px] leading-7 text-foreground-700">
                  我们的读者大多活跃在社交平台上，见过太多标题党，也吃过太多反转的亏。
                  他们不缺信息，缺的是一个能把信息对齐、把脉络理顺的地方。
                </p>
                <p className="text-[15px] leading-7 text-foreground-700">
                  所以瓜田速递的定位很明确：不做观点输出型媒体，做一个可信赖的信息整理者。
                  你在这里看到的每一条内容，都应该能回答三个问题——发生了什么、谁说了什么、接下来看什么。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  {[
                    { icon: "ri-flashlight-fill", label: "快", desc: "突发类首发平均 6 分钟" },
                    { icon: "ri-links-line", label: "全", desc: "时间线与多方说法齐全" },
                    { icon: "ri-shield-check-line", label: "稳", desc: "不确认不写成结论" },
                  ].map((item) => (
                    <div key={item.label} className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                      <i className={`${item.icon} text-xl text-primary-500`} aria-hidden="true"></i>
                      <p className="mt-3 font-heading text-lg font-black text-foreground-950">{item.label}</p>
                      <p className="mt-1 text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="principle"
          aria-label="内容原则"
          className="w-full bg-background-100/70 border-y border-background-200"
        >
          <div className="w-full px-4 md:px-8 py-12 md:py-20">
            <div className="max-w-2xl mb-10">
              <p className="text-xs font-label text-primary-600 tracking-widest mb-3">PRINCIPLES</p>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                内容原则
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                这四条是我们所有内容的底线。它们决定了我们会发什么，也决定了我们不会发什么。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {PRINCIPLES.map((item, index) => (
                <article
                  key={item.title}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
                >
                  <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                      <span className="text-xs font-black text-primary-400">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section aria-label="发展理念" className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 py-12 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-5">
                <p className="text-xs font-label text-secondary-700 tracking-widest mb-3">OUR STORY</p>
                <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 leading-snug">
                  从一条快讯开始
                </h2>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                  我们没有宏大的起点，只是想在信息乱成一团的时候，先把顺序理清楚。
                  后来发现，愿意为「清楚」停留的人，比想象中多得多。
                </p>
              </div>
              <ol className="lg:col-span-7 relative border-l-2 border-background-200 pl-6 md:pl-8 space-y-8">
                {MILESTONES.map((item) => (
                  <li key={item.title} className="relative">
                    <span className="absolute -left-[35px] md:-left-[43px] top-1 w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50"></span>
                    <p className="text-xs font-label text-accent-700">{item.date}</p>
                    <h3 className="mt-1.5 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section aria-label="用户口碑" className="w-full bg-secondary-100/70 border-y border-secondary-200">
          <div className="w-full px-4 md:px-8 py-12 md:py-20">
            <div className="max-w-2xl mb-8">
              <p className="text-xs font-label text-secondary-700 tracking-widest mb-3">VOICES</p>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                读者怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {testimonials.map((item) => (
                <figure
                  key={item.name}
                  className="flex flex-col rounded-lg bg-background-50 border border-secondary-200 p-6"
                >
                  <i className="ri-double-quotes-l text-2xl text-secondary-400" aria-hidden="true"></i>
                  <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed flex-1">
                    {item.text}
                  </blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-background-200 text-sm font-label font-medium text-foreground-900">
                    {item.name}
                    <span className="ml-2 text-xs font-normal text-foreground-500">{item.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" aria-label="联系方式" className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 py-12 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-5">
                <p className="text-xs font-label text-primary-600 tracking-widest mb-3">CONTACT</p>
                <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                  联系与爆料
                </h2>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                  如果你掌握了一手线索，或者发现我们文中的信息有误，都可以直接发邮件给我们。
                  编辑团队会逐一查看，真实有效的线索会优先安排跟进。
                </p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a
                  href="mailto:tips@guatian-express.com"
                  className="group rounded-lg border border-background-200 bg-background-100/60 p-6 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-send-line text-xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">投稿与爆料</h3>
                  <p className="mt-1.5 text-sm text-foreground-600 break-all">tips@guatian-express.com</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-label text-primary-600 group-hover:gap-2 transition-all">
                    发送邮件 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </span>
                </a>
                <div className="rounded-lg border border-background-200 bg-background-100/60 p-6">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-customer-service-2-line text-xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">内容更正</h3>
                  <p className="mt-1.5 text-sm text-foreground-600">编辑部值守：每日 07:00 – 次日 01:00</p>
                  <p className="mt-1 text-xs text-foreground-500 leading-relaxed">
                    请附上文章链接与具体说明，我们会在核实后尽快处理。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SubscribeSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}